import React, { useState, useCallback } from 'react'
import { Grid, Chip } from '@material-ui/core'
import WarningIcon from '@material-ui/icons/Warning'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import InfoIcon from '@material-ui/icons/Info'
import AdminPage from '../../../admin/shared/AdminPage'
import OpalCard from '../../../../opal/OpalCard'
import InlineCardHeader from '../../../../molecules/Card/InlineCardHeader'
import SydButton from '../../../../commonDesign/Button'
import SydLabel from '../../../../commonDesign/SydLabel'
import SydInput from '../../../../commonDesign/SydInput'
import CodeBlock from '../../../../atoms/CodeBlock'
import { useAnalyzeDqlQuery } from '../../../../../api/denali'
import { decipherError } from '../../../../../utils/decipherError'

/**
 * Generate a markdown diagnostic report for AI agent consumption
 * Uses the same output as the main DQL parser pipeline
 */
function generateDiagnosticReport (dqlQuery, result) {
  const lines = []

  lines.push('# DQL Diagnostic Report')
  lines.push('')
  lines.push('## Query')
  lines.push('```sql')
  lines.push(dqlQuery)
  lines.push('```')
  lines.push('')

  // Pipeline info
  lines.push('## Pipeline Info')
  lines.push(`- Used main parser: ${result.pipelineInfo?.usedMainParser ? 'Yes' : 'No (fallback)'}`)
  if (result.pipelineInfo?.sql) {
    lines.push('- Generated SQL: Yes')
  }
  lines.push('')

  // Errors first
  if (result.errors && result.errors.length > 0) {
    lines.push('## ERRORS')
    result.errors.forEach(err => {
      lines.push(`- **${err.phase}**: ${err.message}`)
      if (err.details) lines.push(`  - Details: ${err.details}`)
    })
    lines.push('')
  }

  // Table References
  if (result.tableReferences && result.tableReferences.length > 0) {
    lines.push('## Table References')
    lines.push('| Schema | Table | Alias |')
    lines.push('|--------|-------|-------|')
    result.tableReferences.forEach(table => {
      lines.push(`| ${table.schema || '(none)'} | ${table.table} | ${table.alias || '(none)'} |`)
    })
    lines.push('')
  }

  // Extraction Summary
  const analysis = result.astColumnAnalysis
  if (analysis) {
    lines.push('## Column Extraction Analysis')
    lines.push('**This analyzes the output from the actual DQL parser pipeline.**')
    lines.push('')
    lines.push(`- Total SELECT columns in final AST: ${analysis.allSelectColumns?.length || 0}`)
    lines.push(`- Columns extracted by columnReferenceExtractor: ${analysis.extractedCount}`)
    lines.push(`- Columns NOT extracted: ${analysis.unextractedCount}`)

    if (analysis.hasIssues) {
      lines.push(`- **ISSUE**: ${analysis.unextractedCount} column(s) not extracted`)
      lines.push(`- Unextracted expression types: ${analysis.unextractedTypes.join(', ')}`)
    } else {
      lines.push('- Status: All columns extracted successfully')
    }
    lines.push('')

    // Unextracted Columns (the important part for debugging)
    if (analysis.unextractedCount > 0) {
      lines.push('### Unextracted Columns (REQUIRES FIX)')
      lines.push('| Expression | Type | Alias | Context | Reason |')
      lines.push('|------------|------|-------|---------|--------|')
      analysis.allSelectColumns
        .filter(c => !c.extracted)
        .forEach(col => {
          lines.push(`| \`${col.rawExpression}\` | ${col.expressionType} | ${col.alias || '(none)'} | ${col.context} | ${col.extractionReason} |`)
        })
      lines.push('')
    }
  }

  // Extracted Column References
  if (result.columnReferences && result.columnReferences.length > 0) {
    lines.push('## Extracted Column References')
    lines.push(`Total: ${result.columnReferences.length} columns (after wildcard expansion)`)
    lines.push('')
    lines.push('| Column | Table/Alias | Alias | Is Wildcard |')
    lines.push('|--------|-------------|-------|-------------|')
    result.columnReferences.slice(0, 50).forEach(col => {
      lines.push(`| ${col.column} | ${col.table || '(none)'} | ${col.alias || '(none)'} | ${col.isWildcard ? 'Yes' : 'No'} |`)
    })
    if (result.columnReferences.length > 50) {
      lines.push(`| ... | ... | ... | (${result.columnReferences.length - 50} more) |`)
    }
    lines.push('')
  }

  // Catalog Context
  if (result.catalogContext?.matchedViews?.length > 0) {
    lines.push('## Data Catalog Context')
    result.catalogContext.matchedViews.forEach(view => {
      lines.push(`### ${view.schema}.${view.tableCode} (key: ${view.key})`)
      lines.push(`- View Name: ${view.viewName}`)
      lines.push(`- Datapoints: ${view.datapointCount}`)
      if (view.datapoints && view.datapoints.length > 0) {
        lines.push('- Available columns:')
        view.datapoints.forEach(dp => {
          lines.push(`  - \`${dp.accessor}\` (${dp.type}): ${dp.displayName}`)
        })
      }
      lines.push('')
    })
  }

  // Warnings
  if (result.warnings && result.warnings.length > 0) {
    lines.push('## Warnings')
    result.warnings.forEach(warn => {
      lines.push(`- **${warn.phase}**: ${warn.message}`)
    })
    lines.push('')
  }

  // Recommendations
  if (result.recommendations && result.recommendations.length > 0) {
    lines.push('## Recommendations for Fix')
    result.recommendations.forEach((rec, idx) => {
      lines.push(`### ${idx + 1}. ${rec.description}`)
      lines.push(`- **Priority**: ${rec.priority}`)
      lines.push(`- **File**: \`${rec.file}\``)
      lines.push(`- **Function**: \`${rec.function}\` (line ~${rec.line})`)
      if (rec.suggestedCode) {
        lines.push('- **Suggested Code**:')
        lines.push('```javascript')
        lines.push(rec.suggestedCode.trim())
        lines.push('```')
      }
      lines.push('')
    })
  }

  // Generated SQL (if available)
  if (result.pipelineInfo?.sql) {
    lines.push('## Generated SQL')
    lines.push('```sql')
    lines.push(result.pipelineInfo.sql)
    lines.push('```')
    lines.push('')
  }

  // Full AST
  lines.push('## Full AST (Post-Expansion)')
  lines.push('```json')
  lines.push(JSON.stringify(result.ast, null, 2))
  lines.push('```')
  lines.push('')

  // All Select Column Analysis
  if (analysis?.allSelectColumns) {
    lines.push('## All SELECT Column Nodes (AST Analysis)')
    lines.push('```json')
    lines.push(JSON.stringify(analysis.allSelectColumns.map(c => ({
      expressionType: c.expressionType,
      alias: c.alias,
      rawExpression: c.rawExpression,
      extracted: c.extracted,
      extractionReason: c.extractionReason,
      context: c.context,
      depth: c.depth
    })), null, 2))
    lines.push('```')
  }

  return lines.join('\n')
}

function ParseDqlAnalysisTool () {
  const [dqlQuery, setDqlQuery] = useState('')
  const [analysisResult, setAnalysisResult] = useState(null)
  const [error, setError] = useState(null)
  const [copied, setCopied] = useState(false)

  const { mutateAsync: analyzeDql, isLoading: analyzing } = useAnalyzeDqlQuery()

  const handleAnalyze = useCallback(async () => {
    if (!dqlQuery.trim()) return

    try {
      setError(null)
      setCopied(false)
      const result = await analyzeDql({ dql: dqlQuery })
      setAnalysisResult(result)
    } catch (err) {
      setError(decipherError(err))
      setAnalysisResult(null)
    }
  }, [dqlQuery, analyzeDql])

  const handleCopyReport = useCallback(() => {
    if (!analysisResult) return

    const report = generateDiagnosticReport(dqlQuery, analysisResult)
    navigator.clipboard.writeText(report)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }, [dqlQuery, analysisResult])

  const analysis = analysisResult?.astColumnAnalysis
  const columnRefs = analysisResult?.columnReferences || []

  return (
    <AdminPage>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <OpalCard size='sm'>
            <InlineCardHeader>
              DQL Query Analysis Tool
            </InlineCardHeader>

            <div style={{ padding: '16px' }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <p>Analyze a DQL query using the <strong>same parser pipeline</strong> as real queries. This shows exactly what columns are extracted after wildcard expansion.</p>
                </Grid>

                <Grid item xs={12}>
                  <SydLabel required label='DQL Query' helperText='Example: SELECT cs.clientId, COUNT(*) as rowCount FROM series.client_snapshot cs GROUP BY cs.clientId'>
                    <SydInput
                      value={dqlQuery}
                      onChange={(e) => setDqlQuery(e.target.value)}
                      multiline
                      rows={8}
                      placeholder='SELECT ...'
                    />
                  </SydLabel>
                </Grid>

                <Grid item xs={12}>
                  {error ? <div style={{ color: 'red', marginBottom: '8px' }}>{error}</div> : null}
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <SydButton
                      onClick={handleAnalyze}
                      processing={analyzing}
                      disabled={!dqlQuery.trim()}
                      icon='play_arrow'
                    >
                      Analyze Query
                    </SydButton>
                    {analysisResult && (
                      <SydButton
                        onClick={handleCopyReport}
                        variant='outline'
                        icon={copied ? 'check' : 'content_copy'}
                      >
                        {copied ? 'Copied!' : 'Copy Diagnostic Report'}
                      </SydButton>
                    )}
                  </div>
                </Grid>

                {analysisResult && (
                  <>
                    {/* Pipeline Info Banner */}
                    <Grid item xs={12}>
                      <div style={{
                        padding: '12px',
                        backgroundColor: analysisResult.pipelineInfo?.usedMainParser ? '#e7f3ff' : '#fff3cd',
                        border: `1px solid ${analysisResult.pipelineInfo?.usedMainParser ? '#0d6efd' : '#ffc107'}`,
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                      >
                        <InfoIcon style={{ color: analysisResult.pipelineInfo?.usedMainParser ? '#0d6efd' : '#856404' }} />
                        <div>
                          {analysisResult.pipelineInfo?.usedMainParser ? (
                            <strong>Using main DQL parser pipeline (same as real queries)</strong>
                          ) : (
                            <>
                              <strong>Fallback mode</strong>
                              <div style={{ fontSize: '0.875rem' }}>Main parser failed - showing basic parse results</div>
                            </>
                          )}
                        </div>
                      </div>
                    </Grid>

                    {/* Status Banner */}
                    <Grid item xs={12}>
                      {analysis?.hasIssues ? (
                        <div style={{
                          padding: '12px',
                          backgroundColor: '#fff3cd',
                          border: '1px solid #ffc107',
                          borderRadius: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                        >
                          <WarningIcon style={{ color: '#856404' }} />
                          <div>
                            <strong>{analysis.unextractedCount} column(s) not extracted</strong>
                            <div style={{ fontSize: '0.875rem' }}>
                              Expression types not handled: {analysis.unextractedTypes.join(', ')}
                            </div>
                          </div>
                        </div>
                      ) : analysis ? (
                        <div style={{
                          padding: '12px',
                          backgroundColor: '#d4edda',
                          border: '1px solid #28a745',
                          borderRadius: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                        >
                          <CheckCircleIcon style={{ color: '#155724' }} />
                          <strong>All columns extracted successfully</strong>
                        </div>
                      ) : null}
                    </Grid>

                    {/* Errors */}
                    {analysisResult.errors?.length > 0 && (
                      <Grid item xs={12}>
                        <h4 style={{ color: '#dc3545' }}>Errors</h4>
                        {analysisResult.errors.map((err, idx) => (
                          <div
                            key={idx} style={{
                              padding: '8px',
                              backgroundColor: '#f8d7da',
                              border: '1px solid #f5c6cb',
                              borderRadius: '4px',
                              marginBottom: '8px'
                            }}
                          >
                            <strong>{err.phase}:</strong> {err.message}
                          </div>
                        ))}
                      </Grid>
                    )}

                    {/* Extraction Summary */}
                    <Grid item xs={12} md={6}>
                      <h4>Extraction Summary</h4>
                      <ul>
                        <li>Tables found: {analysisResult.tableReferences?.length || 0}</li>
                        <li>Total columns in final AST: {analysis?.allSelectColumns?.length || 0}</li>
                        <li>Columns extracted: {analysis?.extractedCount || 0}</li>
                        <li>Columns not extracted: {analysis?.unextractedCount || 0}</li>
                      </ul>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <h4>Catalog Matches</h4>
                      <ul>
                        {(analysisResult.catalogContext?.matchedViews || []).map((view, idx) => (
                          <li key={idx}>
                            <strong>{view.schema}.{view.tableCode}</strong> ({view.datapointCount} datapoints)
                          </li>
                        ))}
                        {analysisResult.catalogContext?.matchedViews?.length === 0 && (
                          <li style={{ color: '#666' }}>No catalog views matched</li>
                        )}
                      </ul>
                    </Grid>

                    {/* Unextracted Columns (if any) */}
                    {analysis?.unextractedCount > 0 && (
                      <Grid item xs={12}>
                        <h4 style={{ color: '#856404' }}>Unextracted Columns</h4>
                        <div style={{ overflowX: 'auto' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                            <thead>
                              <tr style={{ backgroundColor: '#f5f5f5' }}>
                                <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Expression</th>
                                <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Type</th>
                                <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Alias</th>
                                <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Reason</th>
                              </tr>
                            </thead>
                            <tbody>
                              {analysis.allSelectColumns
                                .filter(c => !c.extracted)
                                .map((col, idx) => (
                                  <tr key={idx}>
                                    <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>
                                      <code>{col.rawExpression}</code>
                                    </td>
                                    <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>
                                      <Chip label={col.expressionType} size='small' />
                                    </td>
                                    <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>
                                      {col.alias || '-'}
                                    </td>
                                    <td style={{ padding: '8px', borderBottom: '1px solid #eee', fontSize: '0.8rem', color: '#666' }}>
                                      {col.extractionReason}
                                    </td>
                                  </tr>
                                ))}
                            </tbody>
                          </table>
                        </div>
                      </Grid>
                    )}

                    {/* Extracted Columns */}
                    <Grid item xs={12}>
                      <h4>Extracted Column References ({columnRefs.length})</h4>
                      {columnRefs.length > 0 ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                          {columnRefs.slice(0, 50).map((col, idx) => (
                            <Chip
                              key={idx}
                              label={col.alias || col.column}
                              variant='outlined'
                              size='small'
                              title={`${col.table || '(no table)'}.${col.column}${col.alias ? ` AS ${col.alias}` : ''}`}
                            />
                          ))}
                          {columnRefs.length > 50 && (
                            <Chip label={`+${columnRefs.length - 50} more`} size='small' />
                          )}
                        </div>
                      ) : (
                        <p style={{ color: '#666' }}>No columns extracted</p>
                      )}
                    </Grid>

                    {/* Recommendations */}
                    {analysisResult.recommendations?.length > 0 && (
                      <Grid item xs={12}>
                        <h4>Recommendations</h4>
                        {analysisResult.recommendations.map((rec, idx) => (
                          <div
                            key={idx} style={{
                              padding: '12px',
                              backgroundColor: '#e3f2fd',
                              border: '1px solid #2196f3',
                              borderRadius: '4px',
                              marginBottom: '8px'
                            }}
                          >
                            <strong>{rec.description}</strong>
                            <div style={{ fontSize: '0.875rem', marginTop: '4px' }}>
                              File: <code>{rec.file}</code> - Function: <code>{rec.function}</code>
                            </div>
                          </div>
                        ))}
                      </Grid>
                    )}

                    {/* Full Response */}
                    <Grid item xs={12}>
                      <h4>Full Diagnostic Response</h4>
                      <CodeBlock language='json' code={JSON.stringify(analysisResult, null, 2)} />
                    </Grid>
                  </>
                )}
              </Grid>
            </div>
          </OpalCard>
        </Grid>
      </Grid>
    </AdminPage>
  )
}

export default ParseDqlAnalysisTool
